import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { contactInfo } from '../data/mock';
import { Mail, Phone, Smartphone, MapPin, Clock, Facebook, Instagram, Twitter, Linkedin } from 'lucide-react';

const Footer = () => {
  const { t, language } = useLanguage();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center overflow-hidden">
                <img
                  src="/logo.png"
                  alt="Kar Yemek Logo"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<span class="text-white font-bold text-lg">KY</span>';
                  }}
                />
              </div>
              <div>
                <h3 className="text-xl font-bold">Kar Yemek</h3>
                <p className="text-amber-400 text-sm">Sanayi</p>
              </div>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">
              {language === 'tr'
                ? '1998\'den beri kalite ve güvenle hizmetinizdeyiz.'
                : 'Serving you with quality and trust since 1998.'}
            </p>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-amber-400">{t('contact.title')}</h3>
            <ul className="space-y-4">
              <li className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors">
                <Mail className="w-5 h-5 text-amber-500" />
                <a href={`mailto:${contactInfo.email}`}>{contactInfo.email}</a>
              </li>
              <li className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors">
                <Phone className="w-5 h-5 text-amber-500" />
                <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}>{contactInfo.phone}</a>
              </li>
              <li className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors">
                <Smartphone className="w-5 h-5 text-amber-500" />
                <a href={`tel:${contactInfo.gsm.replace(/\s/g, '')}`}>{contactInfo.gsm}</a>
              </li>
              <li className="flex items-start gap-3 text-gray-400">
                <MapPin className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <span>{contactInfo.address}</span>
              </li>
              <li className="flex items-center gap-3 text-gray-400">
                <Clock className="w-5 h-5 text-amber-500" />
                <span>{language === 'tr' ? contactInfo.workingHours : contactInfo.workingHoursEn}</span>
              </li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-amber-400">
              {language === 'tr' ? 'Hızlı Linkler' : 'Quick Links'}
            </h3>
            <ul className="space-y-3">
              <li>
                <a href="#hakkimizda" className="text-gray-400 hover:text-amber-400 transition-colors">
                  {t('kurumsal.hakkimizda')}
                </a>
              </li>
              <li>
                <a href="#hizmetler" className="text-gray-400 hover:text-amber-400 transition-colors">
                  {t('nav.hizmetler')}
                </a>
              </li>
              <li>
                <a href="#galeri" className="text-gray-400 hover:text-amber-400 transition-colors">
                  {t('nav.galeri')}
                </a>
              </li>
              <li>
                <a href="#haberler" className="text-gray-400 hover:text-amber-400 transition-colors">
                  {t('nav.haberler')}
                </a>
              </li>
              <li>
                <a href="#iletisim" className="text-gray-400 hover:text-amber-400 transition-colors">
                  {t('nav.iletisim')}
                </a>
              </li>
            </ul>
          </div>

          {/* Social Media & Map */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-amber-400">
              {language === 'tr' ? 'Bizi Takip Edin' : 'Follow Us'}
            </h3>
            <div className="flex gap-4 mb-6">
              <a
                href="#"
                className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-amber-500 transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="#"
                className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-amber-500 transition-colors"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="#"
                className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-amber-500 transition-colors"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a
                href="#"
                className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-amber-500 transition-colors"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
            
            {/* Mini Map */}
            <div className="rounded-lg overflow-hidden h-32">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3041.0!2d29.97!3d40.05!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDDCsDAzJzAwLjAiTiAyOcKwNTgnMTIuMCJF!5e0!3m2!1str!2str!4v1699999999999!5m2!1str!2str"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Konum"
              ></iframe>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm">
              © {currentYear} Kar Yemek Sanayi. {t('footer.rights')}
            </p>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <a href="#" className="hover:text-amber-400 transition-colors">
                {language === 'tr' ? 'Gizlilik Politikası' : 'Privacy Policy'}
              </a>
              <a href="#" className="hover:text-amber-400 transition-colors">
                {language === 'tr' ? 'Kullanım Şartları' : 'Terms of Use'}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
